import { css } from "@emotion/react";
import styled from "@emotion/styled";

import { ShadowToken, SpacingToken } from "@livechat/design-system-react-components";

import { styleCss } from "components/layout/utils";
import { ResponsiveBreakpoint, ResponsiveBreakpointType } from "constants/responsive-breakpoint";
import { contentPadding } from "routes/styled";
import { withVar } from "themes/helpers";

export const SettingsContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${withVar(SpacingToken.Spacing4)};
  padding-inline: ${withVar(SpacingToken.Spacing16)};
  padding-block: ${withVar(SpacingToken.Spacing8)};

  @media screen and (max-width: ${ResponsiveBreakpoint[ResponsiveBreakpointType.Mobile]}px) {
    padding-inline: ${withVar(SpacingToken.Spacing4)};
    padding-block: ${withVar(SpacingToken.Spacing2)};
  }
`;

export const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  width: 100%;
  height: 100%;
`;

export const InlineForm = styled.form`
  display: flex;
  align-items: center;
`;

/**
 * Don't add any new nested rules here. In new pages, use components created below.
 * It's hard to keep it clean and understandable with so many dependent rules.
 * In the end we want to move away from this.
 */
export const ContentWrapper = styled.div`
  flex: 1;
  overflow: auto;
  ${contentPadding};
  color: ${
    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
    (props: any) => props.theme.primaryColor
  };

  h2 {
    display: flex;
    align-items: center;
    font-weight: 600;
    font-size: 16px;
    line-height: 21px;
    color: ${
      // FIXME: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
      (props: any) => props.theme.primaryColor
    };
    padding-bottom: 6px;
    margin-top: 40px;
    margin-bottom: var(${SpacingToken.Spacing3});
    box-shadow: var(${ShadowToken.DividerBottom});

    &:first-child {
      margin-top: 0;
    }
  }

  a {
    text-decoration: none;

    &:not([type="button"]) {
      color: ${
        // FIXME: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
        (props: any) => props.theme.linkColor
      };
    }

    &:hover {
      text-decoration: underline;
    }
  }
`;

export const SettingsContentWrapper = styled.div`
  flex: 1;
  overflow: auto;
  ${contentPadding};
  max-width: 800px;
`;

export const SettingsHeader = styled.h2<{ marginTop?: string }>`
  display: flex;
  align-items: center;
  font-weight: 600;
  font-size: 16px;
  line-height: 21px;
  ${styleCss("marginTop", undefined, true)}
  color: ${
    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
    (props: any) => props.theme.primaryColor
  };
  padding-bottom: 10px;
  margin-bottom: 24px;
  border-bottom: 1px solid
    ${
      // FIXME: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
      (props: any) => props.theme.borderColor
    };
`;

export const SettingsParagraph = styled.p`
  font-size: 14px;
  color: ${
    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
    (props: any) => props.theme.primaryColor
  };
  line-height: 1.4;
  margin: 1em 0;

  &:first-child {
    margin-top: 0;
  }
  &:last-child {
    margin-bottom: 0;
  }

  h2 + & {
    margin-top: -0.5em;
  }
`;

export const SettingsTable = styled.table`
  table-layout: fixed;
  margin: 2em 0;
  width: 100%;

  th,
  td {
    font-size: 14px;
    color: ${
      // FIXME: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
      (props: any) => props.theme.primaryColor
    };
    line-height: 1.4;
    text-align: left;
    font-weight: normal;
    vertical-align: middle;
    border-bottom: 1px solid
      ${
        // FIXME: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
        (props: any) => props.theme.borderColor
      };
  }

  th {
    padding: 10px 20px;
    @media (max-width: ${
        // FIXME: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
        (props: any) => props.theme.mobileBreakpoint
      }) {
      padding: 10px;
    }
  }

  td {
    padding: 20px;
    @media (max-width: ${
        // FIXME: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
        (props: any) => props.theme.mobileBreakpoint
      }) {
      padding: 10px;
    }
  }

  tbody tr {
    transition: background-color 0.25s;

    .with-hover {
      display: none;
    }

    &:hover {
      background-color: ${
        // FIXME: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
        (props: any) => props.theme.table.highlightedRow.backgroundColor
      };
      .without-hover {
        display: none;
      }
      .with-hover {
        display: flex;
      }
    }
  }
`;

export const HoverWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;

  span {
    margin-left: 20px;
    &:first-child {
      margin-left: 0;
    }
  }
`;
export const SettingsActionButtonsWrapper = styled.div`
  display: flex;
  align-items: center;
  @media (max-width: ${
      // FIXME: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
      (props: any) => props.theme.mobileBreakpoint
    }) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

export const errorIconStyles = css`
  svg {
    width: 64px;
    height: 64px;
  }
`;
